import React, {useEffect} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {CHECKOUT_DELIVERY_STEP} from 'constants/analytics/events/checkout';
import {ApplicationStoreType} from 'desktop/store';
import {flags, isUhcEnabled} from 'libs/rollout';

import {
    setEmailCheckboxStatus,
    setEmailCheckboxVisibility,
    setSmsCheckboxBeenShowed,
    setSmsCheckboxStatus,
} from '../../store/reducers/newsletterSubscription/newsletterSubscription';
import {getBillingAddress as getSideReviewBillingAddress} from '../../store/selectors/billingAddress/billingAddress';
import {
    getEmailSubscriptionCheckboxStatus,
    getEmailSubscriptionVisibility,
    getSmsSubscriptionCheckboxStatus,
    getSmsSubscriptionVisibility,
} from '../../store/selectors/newsletterSubscription/newsletterSubscription';
import {getIsCartHasLuxuryItem} from '../../store/selectors/orderReviewItems/orderReviewItems';
import {getShippingAddress as getSideReviewShippingAddress} from '../../store/selectors/shippingAddress/shippingAddress';
import {autoSubscriptionEnabled} from '../../utils/newsletterSubscription/autoSubscriptionEnabled';
import {useSetSubscriptionEmail} from '../../utils/newsletterSubscription/useSetSubscriptionEmail';
import {useViewCheckbox} from '../../utils/newsletterSubscription/useViewCheckbox';

import {PropsType} from './newsletterSubscriptionType';
import {SubscriptionPhoneDescription} from './subscriptionPhoneDescription/subscriptionPhoneDescription';
import {SubscriptionText} from './subscriptionText';
import {usePhoneSubscriptionRequest} from './usePhoneSubscriptionRequest';

import s from './newsletterSubscription.scss';

const checkIsSubscriptionNonExistent = ({
    emailSubscription,
    isLuxurySpecial,
    isSubscriptionPhoneEnabled,
}: {
    isSubscriptionPhoneEnabled?: boolean | null;
    emailSubscription: {isChecked: boolean; isShown: boolean};
    isLuxurySpecial: boolean;
}) =>
    isUhcEnabled() ||
    isLuxurySpecial ||
    (!emailSubscription.isShown && !isSubscriptionPhoneEnabled);

/**
 * Component for rendering
 * subscribe to newsletter checkbox
 * @params {PropsType} component props
 * @returns {React.ReactElement} rendered component
 */
export const NewsletterSubscription: React.FC<PropsType> = ({
    emailSubscription,
    hasLuxuryItem,
    openPrivacyPolicy,
    openTermsOfUse,
    setEmailCheckbox,
    setEmailVisibility,
    setSmsCheckbox,
    setSmsVisibility,
    sideReviewBillingAddress,
    sideReviewShippingAddress,
    smsSubscription,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const {data, isSubscriptionPhoneEnabled} = usePhoneSubscriptionRequest();
    const isLuxurySpecial = flags.dLuxurySpecialExperience.isEnabled() && hasLuxuryItem;

    useEffect(() => {
        if (data)
            sendDesktopAnalyticsEvent(
                CHECKOUT_DELIVERY_STEP,
                {
                    message: `SMS Subscription Status - ${data.isSmsSubscribed ? 'subscribed' : 'unsubscribed'}`,
                },
                false,
            );
    }, [data]);

    const {checkboxRef, wrapperRef} = useViewCheckbox({
        emailShowed: emailSubscription.isShown,
        isSubscriptionPhoneEnabled,
        smsShowed: smsSubscription.isShown,
    });

    /**
     * Email Subscription
     * */
    const isSubscriptionEnabledForLocation = autoSubscriptionEnabled({
        billingAddress: sideReviewBillingAddress,
        shippingAddress: sideReviewShippingAddress,
    });

    useSetSubscriptionEmail(setEmailVisibility, setEmailCheckbox, isSubscriptionEnabledForLocation);

    useEffect(() => {
        setSmsVisibility(Boolean(isSubscriptionPhoneEnabled));
    }, [setSmsVisibility, isSubscriptionPhoneEnabled]);

    return (
        <div ref={wrapperRef}>
            {checkIsSubscriptionNonExistent({
                emailSubscription,
                isLuxurySpecial,
                isSubscriptionPhoneEnabled,
            }) ? null : (
                <div
                    className={classNames(s.wrapper, {
                        [s.facelift]: isFacelift,
                        [s.wrapperCheckoutCheckboxesPosition]: emailSubscription.isShown,
                        [s.wrapperCheckoutSmsSubscription]:
                            emailSubscription.isShown || isSubscriptionPhoneEnabled,
                    })}
                    ref={checkboxRef}
                >
                    <SubscriptionText
                        emailSubscription={emailSubscription}
                        isPhoneSubscriptionEnabled={isSubscriptionPhoneEnabled}
                        openPrivacyPolicy={openPrivacyPolicy}
                        openTermsOfUse={openTermsOfUse}
                        setEmailCheckbox={setEmailCheckbox}
                        setSmsCheckbox={setSmsCheckbox}
                        smsSubscription={smsSubscription}
                    />
                    <SubscriptionPhoneDescription
                        {...{
                            isEnabled: isSubscriptionPhoneEnabled,
                            openPrivacyPolicy,
                            openTermsOfUse,
                        }}
                    />
                </div>
            )}
        </div>
    );
};

const mapDispatchToProps = {
    setEmailCheckbox: setEmailCheckboxStatus,
    setEmailVisibility: setEmailCheckboxVisibility,
    setSmsCheckbox: setSmsCheckboxStatus,
    setSmsVisibility: setSmsCheckboxBeenShowed,
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    emailSubscription: {
        isChecked: getEmailSubscriptionCheckboxStatus(store),
        isShown: getEmailSubscriptionVisibility(store),
    },
    hasLuxuryItem: getIsCartHasLuxuryItem(store),
    sideReviewBillingAddress: getSideReviewBillingAddress(store),
    sideReviewShippingAddress: getSideReviewShippingAddress(store),
    smsSubscription: {
        isChecked: getSmsSubscriptionCheckboxStatus(store),
        isShown: getSmsSubscriptionVisibility(store),
    },
});

export const NewsletterSubscriptionConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(NewsletterSubscription);
